import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface AppShellNavItem {
  label: string;
  href: string;
  icon?: ReactNode;
  active?: boolean;
}

export interface AppShellProps {
  brand?: ReactNode;
  nav: AppShellNavItem[];
  headerRight?: ReactNode;
  /** Renders an anchor for each nav item. Default: <a href={href}>. */
  renderNavLink?: (item: AppShellNavItem, children: ReactNode) => ReactNode;
  children: ReactNode;
  className?: string;
}

export function AppShell({
  brand,
  nav,
  headerRight,
  renderNavLink,
  children,
  className,
}: AppShellProps) {
  const renderLink =
    renderNavLink ??
    ((item: AppShellNavItem, content: ReactNode) => <a href={item.href}>{content}</a>);

  return (
    <div className={cn('flex min-h-screen flex-col bg-zinc-50 text-zinc-900', className)}>
      <header className="flex h-14 items-center gap-4 border-b border-zinc-200 bg-white px-4">
        <div className="font-semibold">{brand}</div>
        <nav className="flex flex-1 items-center gap-1">
          {nav.map((item) => (
            <span key={item.href}>
              {renderLink(
                item,
                <span
                  className={cn(
                    'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium',
                    item.active
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-zinc-600 hover:bg-zinc-100',
                  )}
                >
                  {item.icon}
                  {item.label}
                </span>,
              )}
            </span>
          ))}
        </nav>
        {headerRight && <div className="flex items-center gap-2">{headerRight}</div>}
      </header>
      <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
}
